import React, { useState } from 'react'
import AppLayout from '../components/AppLayout'

const PLANS = [
  {
    id: 'fire', icon: '🔥', title: 'Fire', color: '#c0392b',
    priority: 'LIFE THREATENING — EVACUATE FIRST',
    steps: [
      { urgent: true, text: 'Evacuate immediately. Do not try to fight a large fire. Leave everything.' },
      { urgent: true, text: 'Call 999 (or local emergency number). Give your exact address and what is on fire.' },
      { urgent: false, text: 'Do not go back inside for any reason — not for people, not for animals, not for documents.' },
      { urgent: false, text: 'Call Edward once you are safe outside.' },
      { urgent: false, text: 'Call insurance company as soon as possible. Take photos of damage only after fire service clears the scene.' },
    ],
    contacts: ['Emergency Services: 999', 'Sharon Nankunda: +256773242408', 'Edward Katongole: +256766989006', 'Insurance Company: [add number]']
  },
  {
    id: 'flood', icon: '💧', title: 'Flood / Major Water Leak', color: '#2980b9',
    priority: 'DANGER — Electricity + Water = Lethal',
    steps: [
      { urgent: true, text: 'Turn off electricity at the mains FIRST if it is safe to do so. Electricity + water is lethal.' },
      { urgent: true, text: 'Turn off the water supply at the main valve.' },
      { urgent: false, text: 'Call Edward immediately. Describe what happened and the extent.' },
      { urgent: false, text: 'Photograph everything before cleaning up — for insurance purposes.' },
      { urgent: false, text: 'Call a plumber and your insurance company. Do not attempt major repairs yourself.' },
    ],
    contacts: ['Sharon Nankunda: +256773242408', 'Edward Katongole: +256766989006', 'Plumber: [add number]', 'Insurance: [add number]']
  },
  {
    id: 'power', icon: '⚡', title: 'Power Failure', color: '#f39c12',
    priority: 'Act within 2 hours to protect crops',
    steps: [
      { urgent: false, text: '0–30 min: Check fuse box and circuit breakers. Call electricity provider to check for outage in your area.' },
      { urgent: false, text: '30 min – 2 hrs: Monitor temperature. Call Edward. Consider portable generator if available.' },
      { urgent: false, text: '2–4 hrs: Begin emergency harvest of any near-ready produce. Plants will survive short term.' },
      { urgent: true, text: '4+ hrs: Significant crop risk. Add ice to the fridge. Document everything for insurance.' },
      { urgent: false, text: 'Do not open fridge unnecessarily — every opening raises the temperature.' },
    ],
    contacts: ['Sharon Nankunda: +256773242408', 'Electricity Provider: [add number]', 'Edward Katongole: +256766989006']
  },
  {
    id: 'disease', icon: '🦠', title: 'Crop Disease / Pest Outbreak', color: '#27ae60',
    priority: 'Isolate immediately — stop the spread',
    steps: [
      { urgent: true, text: 'Photograph affected plants BEFORE touching anything. Identification requires photos.' },
      { urgent: true, text: 'Put on gloves. Remove affected plants and place in sealed bags immediately. Dispose OUTSIDE the farm — never compost diseased material.' },
      { urgent: false, text: 'Send photos to Edward. Do not treat until the problem is correctly identified.' },
      { urgent: false, text: 'Post photos in hydroponic grower forums or contact an agricultural extension officer (they are free and very helpful).' },
      { urgent: false, text: 'Once identified: treat only with approved food-safe treatments. Record all treatments.' },
      { urgent: false, text: 'Monitor daily. If spread continues after treatment — call a specialist.' },
    ],
    contacts: ['Sharon Nankunda: +256773242408', 'Edward Katongole: +256766989006', 'Agricultural Extension: [add number]']
  },
  {
    id: 'breakin', icon: '👤', title: 'Break-In / Theft', color: '#555',
    priority: 'Do NOT enter. Preserve evidence.',
    steps: [
      { urgent: true, text: 'If you arrive and anything seems wrong — do NOT enter. Stay outside and call police.' },
      { urgent: true, text: 'Call police immediately — 999. Do not touch or move anything. This preserves evidence.' },
      { urgent: false, text: 'Call Edward. Wait outside for police to arrive.' },
      { urgent: false, text: 'After police clear the scene — photograph everything. Do not clean up until insurance company approves.' },
      { urgent: false, text: 'Call insurance company within 24 hours. Report to police and get a case reference number.' },
    ],
    contacts: ['Police: 999', 'Sharon Nankunda: +256773242408', 'Edward Katongole: +256766989006', 'Insurance: [add number]']
  },
  {
    id: 'injury', icon: '🤕', title: 'Injury on Farm', color: '#8B2E2E',
    priority: 'Serious injury — call emergency services first',
    steps: [
      { urgent: true, text: 'Serious injury: Call emergency services immediately — 999. Keep the person still and calm. Stay on the line.' },
      { urgent: false, text: 'Minor injury: First aid kit is at [LOCATION]. Treat minor wounds. Record in Incident Report.' },
      { urgent: true, text: 'Chemical exposure (skin/eyes): Flush with water for 15 minutes minimum. Call Poison Information. Keep the chemical label to show emergency services.' },
      { urgent: false, text: 'Complete an Incident Report as soon as the person is safe.' },
      { urgent: false, text: 'Serious injuries may be legally notifiable — Edward will handle this.' },
    ],
    contacts: ['Emergency Services: 999', 'Poison Information: [local number]', 'Sharon Nankunda: +256773242408', 'Edward Katongole: +256766989006']
  },
]

export default function Emergency() {
  const [open, setOpen] = useState(null)
  const [printMode, setPrintMode] = useState(false)

  return (
    <AppLayout title="Emergency Plan">
      <div className="section-header">
        <div className="section-icon">🆘</div>
        <div className="section-info">
          <h2>Emergency Response Plan</h2>
          <p>What to do when things go wrong. Stay calm, follow the plan.</p>
        </div>
        <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={() => window.print()}>🖨️ Print & Post on Wall</button>
      </div>

      <div className="alert alert-red" style={{ fontSize: '0.9rem', fontWeight: 600 }}>
        🖨️ PRINT THIS PAGE AND POST IT ON THE FARM WALL. In a real emergency you won't want to search for it on a screen.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
        {PLANS.map(plan => (
          <div key={plan.id} className="card" style={{ borderLeft: `4px solid ${plan.color}`, cursor: 'pointer' }}
            onClick={() => setOpen(open === plan.id ? null : plan.id)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: '1.8rem' }}>{plan.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.1rem', fontWeight: 700, color: plan.color }}>{plan.title}</div>
                <div style={{ fontSize: '0.7rem', fontFamily: "'DM Mono',monospace", color: plan.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{plan.priority}</div>
              </div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{open === plan.id ? '▲' : '▼'}</span>
            </div>

            {open === plan.id && (
              <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                {plan.steps.map((step, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                    <div style={{ width: 26, height: 26, minWidth: 26, background: step.urgent ? plan.color : 'var(--text-muted)', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>{i+1}</div>
                    <div style={{ fontSize: '0.82rem', lineHeight: 1.6, fontWeight: step.urgent ? 700 : 400, color: step.urgent ? plan.color : 'var(--text)' }}>{step.text}</div>
                  </div>
                ))}
                <div style={{ marginTop: 12, padding: 10, background: '#f8f8f0', borderRadius: 8 }}>
                  <div style={{ fontSize: '0.7rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: 6 }}>Key Contacts</div>
                  {plan.contacts.map((c, i) => (
                    <div key={i} style={{ fontSize: '0.8rem', fontWeight: 600, padding: '3px 0' }}>📞 {c}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Key contacts summary */}
      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-title">📞 Emergency Contacts — Always Keep This Updated</div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Contact</th><th>Name</th><th>Phone</th><th>When to Call</th></tr></thead>
            <tbody>
              {[
                ['Emergency Services', '', '999', 'Fire, injury, crime in progress'],
                ['Sharon Nankunda (Managing Director)', 'Sharon Nankunda', '+256773242408', 'Primary contact — any major incident'],
                ['Edward Katongole (Director)', 'Edward Katongole', '+256766989006', 'Secondary contact — major incidents'],
                ['Electrician', '', '[number]', 'Electrical faults, power issues'],
                ['Plumber', '', '[number]', 'Water leaks, pipe bursts'],
                ['Insurance Company', '', '[number]', 'Any damage claim'],
                ['Agricultural Extension Officer', '', '[number]', 'Crop disease, pest identification'],
                ['Equipment Supplier', '', '[number]', 'Pump/equipment breakdown'],
              ].map(([role, name, phone, when]) => (
                <tr key={role}>
                  <td style={{ fontWeight: 600, fontSize: '0.82rem' }}>{role}</td>
                  <td style={{ fontSize: '0.82rem' }}>{name || <span style={{ color: '#ccc' }}>Fill in</span>}</td>
                  <td style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.8rem', color: phone === '[your number]' || phone === '[number]' ? '#ccc' : 'var(--green-mid)', fontWeight: 600 }}>{phone}</td>
                  <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{when}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  )
}
